import { useEffect, useMemo, useRef, useState } from 'react';
import { Step } from 'types/tutorial';
import { normalizeApiHtmlContent, preloadImagesFromHtmlBlocks } from 'utils/html-content';
import './style.scss';

interface VotacaoEtapa2TutorialModalProps {
  steps: Step[];
  onClose: () => void;
  onStartVoting: () => void;
}

export function VotacaoEtapa2TutorialModal({
  steps,
  onClose,
  onStartVoting,
}: VotacaoEtapa2TutorialModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepContentReady, setIsStepContentReady] = useState(false);
  const textContainerRef = useRef<HTMLDivElement>(null);

  const currentStepHtml = useMemo(
    () => normalizeApiHtmlContent(steps[currentStep]?.text || ''),
    [steps, currentStep]
  );

  useEffect(() => {
    const htmlBlocks = steps.map((step) => step?.text || '');
    if (htmlBlocks.length > 0) {
      preloadImagesFromHtmlBlocks(htmlBlocks);
    }
  }, [steps]);

  useEffect(() => {
    const container = textContainerRef.current;
    if (!container) {
      setIsStepContentReady(true);
      return;
    }

    setIsStepContentReady(false);
    const images = Array.from(container.querySelectorAll('img'));

    if (images.length === 0) {
      const raf = requestAnimationFrame(() => setIsStepContentReady(true));
      return () => cancelAnimationFrame(raf);
    }

    let loadedCount = 0;
    let isMounted = true;

    const handleAssetReady = () => {
      loadedCount += 1;
      if (isMounted && loadedCount >= images.length) {
        setIsStepContentReady(true);
      }
    };

    images.forEach((img) => {
      if (img.complete) {
        handleAssetReady();
      } else {
        img.addEventListener('load', handleAssetReady);
        img.addEventListener('error', handleAssetReady);
      }
    });

    return () => {
      isMounted = false;
      images.forEach((img) => {
        img.removeEventListener('load', handleAssetReady);
        img.removeEventListener('error', handleAssetReady);
      });
    };
  }, [currentStepHtml]);

  if (steps.length === 0) {
    return null;
  }

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const isLogoStep = currentStep === 1;

  const handlePrimaryAction = () => {
    if (isLastStep) {
      onStartVoting();
      return;
    }
    setCurrentStep((previousStep) => previousStep + 1);
  };

  const handleBack = () => {
    if (isFirstStep) {
      onClose();
      return;
    }
    setCurrentStep((previousStep) => previousStep - 1);
  };

  return (
    <div className="votacao-etapa2-tutorial-modal">
      <div className="votacao-etapa2-tutorial-modal__card">
        <div
          ref={textContainerRef}
          className={`votacao-etapa2-tutorial-modal__content ${isStepContentReady ? 'is-ready' : ''} ${isLogoStep ? 'is-logo-step' : ''}`}
          dangerouslySetInnerHTML={{ __html: currentStepHtml }}
        />

        <div className="votacao-etapa2-tutorial-modal__indicators">
          {steps.map((_, index) => (
            <button
              key={index}
              className={`votacao-etapa2-tutorial-modal__indicator ${index === currentStep ? 'active' : ''}`}
              onClick={() => setCurrentStep(index)}
              aria-label={`Ir para passo ${index + 1}`}
            />
          ))}
        </div>

        <div className="votacao-etapa2-tutorial-modal__actions">
          <button
            className="votacao-etapa2-tutorial-modal__btn votacao-etapa2-tutorial-modal__btn--secondary"
            onClick={handleBack}
            type="button"
          >
            {isFirstStep ? 'FECHAR' : 'VOLTAR'}
          </button>

          <button
            className="votacao-etapa2-tutorial-modal__btn votacao-etapa2-tutorial-modal__btn--primary"
            onClick={handlePrimaryAction}
            type="button"
          >
            {isLastStep ? 'INICIAR AVALIAÇÃO' : 'PRÓXIMO'}
          </button>
        </div>
      </div>
    </div>
  );
}
